import { useEffect, useRef } from 'react'

const EDGE_THRESHOLD = 80
const MAX_SPEED = 15

/**
 * Auto-scrolls the window when a native HTML5 drag operation moves
 * the cursor within {@link EDGE_THRESHOLD}px of the viewport top or bottom.
 * Scroll speed scales linearly with proximity to the edge.
 */
export function useAutoScrollOnDrag(enabled: boolean) {
  const rafId = useRef<number | null>(null)
  const currentSpeed = useRef(0)

  useEffect(() => {
    if (!enabled) return

    function onDragOver(e: DragEvent) {
      const { clientY } = e
      const vh = window.innerHeight

      let speed = 0
      if (clientY < EDGE_THRESHOLD) {
        speed = -MAX_SPEED * (1 - clientY / EDGE_THRESHOLD)
      } else if (clientY > vh - EDGE_THRESHOLD) {
        speed = MAX_SPEED * (1 - (vh - clientY) / EDGE_THRESHOLD)
      }

      currentSpeed.current = speed

      if (speed !== 0 && rafId.current === null) {
        const tick = () => {
          window.scrollBy({ top: currentSpeed.current })
          rafId.current = requestAnimationFrame(tick)
        }
        rafId.current = requestAnimationFrame(tick)
      } else if (speed === 0) {
        stopScroll()
      }
    }

    function stopScroll() {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
        rafId.current = null
      }
      currentSpeed.current = 0
    }

    document.addEventListener('dragover', onDragOver)
    document.addEventListener('dragend', stopScroll)
    document.addEventListener('drop', stopScroll)

    return () => {
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('dragend', stopScroll)
      document.removeEventListener('drop', stopScroll)
      stopScroll()
    }
  }, [enabled])
}
